import { type ClientId } from '../client/bridge-api-client-types';

/**
 * Primitive values which are serializable over JavaScript contexts.
 */
type SerializablePrimitive = string | number | boolean | null | undefined;

/**
 * Object containing serializable properties.
 * @see {@link SerializablePrimitive} for primitive values.
 */
type SerializableObject = {
	[K in string | number]: SerializableValue;
};

/**
 * Array containing serializable properties.
 * @see {@link SerializablePrimitive} for primitive values.
 */
type SerializableArray = SerializableValue[];

/**
 * Serializable values which can be sent
 * @see {@link SerializablePrimitive} for primitive values.
 */
export type SerializableValue =
	| SerializablePrimitive
	| SerializableObject
	| SerializableArray
	| void;

/**
 * Base properties which all commands must inherit.
 *
 * @description Note: All commands currently include a mandatory {@link senderId} and {@link receiverId}. This is
 * to force producers to be explicit in who they are, and who they are trying to communicate with (who = a client).
 *
 * @description All properties MUST be serializable in their representation. The properties defined here may be sent
 * over-the-wire between frame contexts (e.g. consider cross-origin `iframe`s). See {@link SerializableValue}.
 */
export interface CommandProperties extends Record<string, SerializableValue> {
	/**
	 * The unique identifier of the client sending the command.
	 *
	 * @example `rovo-content-preview`
	 */
	senderId: ClientId;

	/**
	 * The unique identifier of the client that should handle the command.
	 *
	 * @example `rovo-chat`
	 */
	receiverId: ClientId;

	/**
	 * Optional group identifier for commands that should share the same lifecycle.
	 *
	 * @description When a command with a groupId is sent, the timer is reset for all
	 * commands with the same groupId. This allows network streams to produce N commands
	 * and wait for MESSAGE_LIFETIME_IN_MILLIS after the last message before dropping all of them.
	 *
	 * @example `stream-session-123` for commands from the same streaming session
	 */
	groupId?: string;

	/**
	 * Whether this command should be buffered if the receiver is not ready.
	 *
	 * @description By default, commands are buffered when the receiver is not ready.
	 * Set this to `false` for commands that should be dropped immediately if the receiver cannot handle them.
	 *
	 * @default true
	 */
	buffer?: boolean;

	/**
	 * A unique identifier for this specific invocation of the command.
	 *
	 * @description Necessary for handling multiple invocations of a command.
	 * @description Added and handled internally. DO NOT read or write to this value.
	 *
	 * @deprecated
	 */
	_invocationId?: string;
}

/**
 * The base response type expected when handling a given command. Must be serializable over JavaScript contexts.
 */
export type CommandResult = SerializableValue;

/**
 * Base container for a command, providing a named reference to a command being invoked by a bridge API client.
 *
 * @description A command in itself does not contain the properties it is being invoked with. Think of it as a symbol
 * which is invoked along with a separate {@link properties} shape, indicating its parameters.
 *
 * @description Commands are serialized over-the-wire into their class name. Properties are serialized into a set of
 * {@link SerializableValue}s, so that a JS context on the other side can deserialize and handle them.
 */
export abstract class Command<TProps extends CommandProperties, TResult extends CommandResult> {
	/**
	 * A unique, human readable identifier for the command. Differs from {@link Command.name}, which is the JavaScript
	 * -defined name given to a function symbol DO NOT use {@link Command.name} for any in-production purposes. It can
	 * (1) be collapsed into clashing, minified names in the same bundle, or (2) differ across bundles due to differences
	 * in decisions made by JavaScript bundlers in how they minify the name of a symbol.
	 */
	static readonly identifier: string;

	constructor(
		public properties: TProps,
		public result: TResult,
	) {}
}

/**
 * Type helper for a command constructor.
 */
export interface CommandConstructor<
	T extends CommandProperties = CommandProperties,
	R extends CommandResult = CommandResult,
> {
	new (...args: any[]): Command<T, R>;
	/**
	 * @see {@link Command.identifier}
	 */
	readonly identifier: string;
}

/**
 * Marker interface for broadcastable commands.
 *
 * @description Broadcastable commands can be used with the broadcast API. They are regular
 * commands with a marker property to assist with type-level constraints, without changing
 * their runtime behaviour.
 */
export interface BroadcastableCommandConstructor<C extends CommandProperties>
	extends CommandConstructor<C, void> {
	readonly __broadcastable: true;
}

/**
 * Type helper for a command handler.
 */
export type CommandHandler<P extends CommandProperties, R extends CommandResult> = (
	params: P,
) => R | Promise<R>;

/**
 * Type helper for a command with a known sender.
 */
export type CommandPropertiesWithKnownSender<T extends CommandProperties> = {
	[K in keyof T as K extends 'senderId' ? never : K]: T[K];
};

/**
 * Represents the properties of a broadcast command by omitting the `receiverId` property
 * from the given command properties type `C`.
 *
 * @typeParam C - The base command properties type, which must include a `receiverId` property.
 * @remarks
 * This utility type is useful when defining commands that are intended to be broadcast
 * to multiple receivers, where specifying a single `receiverId` is not applicable.
 */
export type BroadcastCommandProperties<C extends CommandProperties> = Omit<C, 'receiverId'>;

/**
 * Options for broadcasting a command to multiple clients.
 *
 * @property filter - An optional function that determines whether a client,
 *   identified by its `clientId`, should receive the broadcasted command.
 *   If provided, only clients for which this function returns `true` will receive the command.
 */
export type BroadcastCommandOptions = {
	filter?: (clientId: ClientId) => boolean;
};

/**
 * Utility type to extract the CommandProperties type from a CommandConstructor.
 */
export type ExtractCommandProperties<T> = T extends CommandConstructor<infer P, any> ? P : never;

/**
 * Utility type to extract the CommandResult type from a CommandConstructor.
 */
export type ExtractCommandResult<T> = T extends CommandConstructor<any, infer R> ? R : never;
